import { useState } from 'react';
import { MuiTextField } from 'components/widgets/mui-text-field/TextField';
import {
    CircularProgress,
    Grid,
    Button,
    FormControl,
    FormLabel,
    RadioGroup,
    Radio,
    FormControlLabel,
    Select,
    InputLabel,
    MenuItem,
    Checkbox,
    ListItemText,
    Alert,
    Typography,
    Tooltip,
} from '@mui/material';
import { Block } from './Block';
import { UseStyleHook } from '../hooks/useStyleHook';
import { MuiSelectSingleChoice } from '../../../../widgets/mui-text-field/SingleChoiceSelect';
import { IValueLabelPair } from 'models/Common';
import { ACCOUNT_TYPE_OPTIONS, CHEQ_ACCOUNT_STATUS_OPTIONS } from '../constant';
import { AccountFormComponentProps } from '../types';
import { Products } from 'services/api/consts';
import { useGetCurrentAccount } from '../hooks/useGetCurrentAccount';
import Dialog from '../../../../widgets/dialog';
import { useDispatch } from 'react-redux';
import { isLoading } from 'redux/slicers/accounts';
import { useSplit } from 'hooks';
import { SPLIT_CONSTANTS } from 'services/split.io';

export const CheqInformation = ({ value, onChange, selectOptions, isNewAccount, isServeDomainTainted, onChangeServingDomain }: AccountFormComponentProps) => {
    const { defaultProps, selectProps, classes } = UseStyleHook();
    const currentAccount = useGetCurrentAccount();
    const showDataType = value.activeLicenses.includes(Products.OSS);
    const [changeDomainConfirmOpen, setChangeDomainConfirmOpen] = useState(false);
    const dispatch = useDispatch();
    const { isOn: isAgentIntentRebrand } = useSplit(SPLIT_CONSTANTS.PD_AGENT_INTENT_REBRAND);
    const dataTypeLabel = isAgentIntentRebrand ? 'Agent Intent Data Type' : 'Defend Data Type';

    const servingDomainValue = !isNewAccount ? currentAccount?.networks?.[0].whiteLabelDomain.domain || value.servingDomain : value.servingDomain;

    return (
        <>
            <Block
                title="CHEQ Information"
                content={
                    <>
                        <Grid item xs={12} md={6}>
                            <FormControl>
                                <FormLabel>Account Type</FormLabel>
                                <RadioGroup row value={value.accountType} onChange={(e) => onChange({ accountType: Number(e.target.value) })} {...defaultProps}>
                                    {ACCOUNT_TYPE_OPTIONS.map((opt: { value: number; label: string }) => (
                                        <FormControlLabel key={opt.value} value={opt.value} control={<Radio />} label={opt.label} />
                                    ))}
                                </RadioGroup>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormControl>
                                <FormLabel>
                                    Account Status
                                    {!!currentAccount?.active &&
                                        Array.isArray(currentAccount?.networks) &&
                                        !currentAccount.networks.some((n: any) => n.active) && (
                                            <Tooltip
                                                componentsProps={{ tooltip: { sx: { bgcolor: 'background.paper', p: 0, maxWidth: 400, boxShadow: 3 } } }}
                                                title={
                                                    <Alert variant="outlined" severity="error" sx={{ fontSize: 14 }}>
                                                        This account is set to Active but none of its networks are active. An account should be active only while at least one network is active.
                                                    </Alert>
                                                }
                                            >
                                                <span
                                                    style={{
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        width: 18,
                                                        height: 18,
                                                        borderRadius: '50%',
                                                        backgroundColor: '#d32f2f',
                                                        color: '#fff',
                                                        fontSize: 13,
                                                        fontWeight: 'bold',
                                                        marginLeft: 6,
                                                        cursor: 'help',
                                                        verticalAlign: 'middle',
                                                    }}
                                                >
                                                    !
                                                </span>
                                            </Tooltip>
                                        )}
                                </FormLabel>
                                <RadioGroup row value={value.cheqAccountStatus} onChange={(e) => onChange({ cheqAccountStatus: Number(e.target.value) })}>
                                    {CHEQ_ACCOUNT_STATUS_OPTIONS.map((opt: { value: number; label: string }) => (
                                        <FormControlLabel key={opt.value} value={opt.value} control={<Radio />} label={opt.label} />
                                    ))}
                                </RadioGroup>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} md={6} marginBottom={0}>
                            <MuiSelectSingleChoice
                                {...selectProps}
                                id="region"
                                label="Region"
                                value={value.region}
                                onChange={(e) => onChange({ region: e.target.value })}
                                options={selectOptions.region}
                            />
                        </Grid>

                        <Grid item xs={12} md={6} marginBottom={0}>
                            <MuiSelectSingleChoice
                                {...selectProps}
                                id="data-location"
                                label="Data Location"
                                value={value.dataLocation}
                                onChange={(e) => onChange({ dataLocation: e.target.value })}
                                options={selectOptions.dataLocation}
                                disabled={!isNewAccount}
                            />
                        </Grid>
                        <Grid item xs={12} style={{}}>
                            <Alert variant="outlined" severity="warning">
                                Region configuration changes the dashboard stats timezone
                            </Alert>
                        </Grid>

                        <Grid item xs={12}>
                            <InputLabel>Active Licenses</InputLabel>
                            <Select
                                multiple
                                id="active-licenses"
                                value={Array.isArray(value?.activeLicenses) ? value.activeLicenses : []}
                                onChange={({ target: { value } }) => onChange({ activeLicenses: Array.isArray(value) ? value : [] })}
                                className={classes.accountForm}
                                renderValue={(selected) =>
                                    Array.isArray(selected) ? selected.map((value) => selectOptions.activeLicenses.find((opt) => opt.value === value)?.label).join(', ') : ''
                                }
                                {...selectProps}
                            >
                                {selectOptions.activeLicenses?.map((p) => (
                                    <MenuItem key={p.value} value={p.value}>
                                        <Checkbox checked={value.activeLicenses.indexOf(p.value) > -1} />
                                        <ListItemText primary={p.label} sx={{ textOverflow: 'ellipsis' }} />
                                    </MenuItem>
                                ))}
                            </Select>
                        </Grid>

                        {
                            <Grid item xs={12} md={6}>
                                {showDataType && (
                                    <>
                                        <InputLabel>{dataTypeLabel}</InputLabel>
                                        <Select
                                            multiple
                                            id="defend-data-type"
                                            value={value?.defendDataType || ''}
                                            onChange={({ target: { value } }) => {
                                                onChange({ defendDataType: Array.isArray(value) ? value : [] });
                                            }}
                                            renderValue={(selected) => (Array.isArray(selected) ? selected.join(', ') : '')}
                                            className={classes.accountForm}
                                            {...selectProps}
                                        >
                                            {selectOptions.defendDataType?.map((p: IValueLabelPair) => (
                                                <MenuItem key={p.value} value={p.value}>
                                                    <Checkbox checked={value.defendDataType.indexOf(p.value) > -1} />
                                                    <ListItemText primary={p.label} sx={{ textOverflow: 'ellipsis' }} />
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </>
                                )}
                            </Grid>
                        }
                        {!isNewAccount && (
                            <>
                                <Grid item xs={12}>
                                    <MuiTextField
                                        disabled
                                        id="serving-domain"
                                        label="Serving Domain"
                                        placeholder="Type here"
                                        value={servingDomainValue}
                                        onChange={(e) => onChange({ servingDomain: e.target.value })}
                                        {...defaultProps}
                                    />
                                </Grid>

                                {isServeDomainTainted && (
                                    <>
                                        <Grid item xs={12} md={6} display="flex" alignItems="flex-end">
                                            <Button
                                                variant="outlined"
                                                color="info"
                                                onClick={() => setChangeDomainConfirmOpen(true)}
                                                disabled={(dispatch(isLoading('getNextAvailableDomain')) || dispatch(isLoading('getAssociatedNetworks'))) as unknown as boolean}
                                                startIcon={
                                                    ((dispatch(isLoading('getNextAvailableDomain')) || dispatch(isLoading('getAssociatedNetworks'))) as unknown as boolean) && (
                                                        <CircularProgress size={16} />
                                                    )
                                                }
                                            >
                                                Change Domain
                                            </Button>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Alert variant="outlined" severity="error">
                                                This domain is blocked by Add-Blockers
                                            </Alert>
                                        </Grid>
                                    </>
                                )}
                                <Grid item xs={12}>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Deduce Integration</Typography>
                                    {!value.deduceEnabled ? (
                                        <Button variant="outlined" color="primary" onClick={() => onChange({ deduceEnabled: 1 })}>
                                        ADD CREDENTIALS
                                        </Button>
                                    ) : (
                                        <Grid display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                                            <MuiTextField
                                                id="deduce-site-id"
                                                label={false}
                                                placeholder="Site ID"
                                                value={value.deduceSite}
                                                onChange={(e) => onChange({ deduceSite: e.target.value })}
                                                sx={{ flex: 1 }}
                                                {...defaultProps}
                                            />
                                            <MuiTextField
                                                id="deduce-api-key"
                                                label={false}
                                                placeholder="Key"
                                                value={value.deduceApiKey}
                                                onChange={(e) => onChange({ deduceApiKey: e.target.value })}
                                                {...defaultProps}
                                            />
                                            <Button
                                                variant="outlined"
                                                color="error"
                                                onClick={() => onChange({ deduceEnabled: 0, deduceSite: '', deduceApiKey: '' })}
                                                sx={{ flexShrink: 0, height: 40 }}
                                            >
                                                Remove
                                            </Button>
                                        </Grid>
                                    )}
                                </Grid>
                            </>
                        )}
                    </>
                }
            />
            <Dialog
                onClose={() => setChangeDomainConfirmOpen(false)}
                isOpen={changeDomainConfirmOpen}
                onSubmit={() => {
                    if (onChangeServingDomain) {
                        onChangeServingDomain();
                    }
                    setChangeDomainConfirmOpen(false);
                }}
                title="Change Serving Domain"
                body="Are you sure you want to change the serving domain?"
                Component={false}
                props={{}}
            />
        </>
    );
};
